import { useQuery } from '@apollo/client/react';
import { ExternalLinkIcon, MessageSquareIcon, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';

import { CustomerFeedbackDialog } from '@/components/CustomerFeedbackDialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '@/components/ui/empty';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useOdooAuth } from '@/contexts/OdooAuthContext';
import { GET_CUSTOMER_REQUESTS } from '@/graphql/customerFeedback';

export default function CustomerRequestsPage() {
  return <CustomerRequests />;
}

const STATUS_VARIANT: Record<
  string,
  | 'blue'
  | 'green'
  | 'orange'
  | 'pastel-blue'
  | 'pastel-slate'
  | 'red'
  | 'yellow'
> = {
  backlog: 'pastel-slate',
  blocked: 'red',
  canceled: 'pastel-slate',
  completed: 'green',
  started: 'orange',
  unstarted: 'pastel-blue'
};

const PRIORITY_VARIANT: Record<number, 'pastel-slate' | 'red'> = {
  0: 'pastel-slate',
  1: 'red'
};

const PRIORITY_LABELS: Record<number, string> = {
  0: 'None',
  1: 'Important'
};

export function CustomerRequests() {
  const { odooAuth } = useOdooAuth();
  const navigate = useNavigate();
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data,
    loading: isLoading,
    refetch
  } = useQuery(GET_CUSTOMER_REQUESTS, {
    fetchPolicy: 'cache-and-network',
    onError: error => {
      console.error('Error fetching customer requests:', error);
      toast.error('Failed to load requests');
    }
  });

  const requests = data?.customerRequests?.requests || [];
  const customerName = data?.customerRequests?.customerName || '';

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date);
  };

  // Parse metadata from description
  const parseDescription = (description: string) => {
    const lines = description.split('\n');
    const metadata: Record<string, string> = {};
    const contentLines: string[] = [];

    for (const line of lines) {
      const match = /\*\*(.+?):\*\*\s*(.+)/.exec(line);
      if (match) {
        metadata[match[1]] = match[2];
      } else if (line.trim()) {
        contentLines.push(line);
      }
    }

    return {
      content: contentLines.join('\n').trim(),
      metadata
    };
  };

  // Filter requests based on selected filters
  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      // Status filter
      if (selectedStatus && request.statusType !== selectedStatus) {
        return false;
      }

      // Priority filter (1 = important, 0 = not important)
      if (selectedPriority) {
        const priorityValue = parseInt(selectedPriority, 10);
        if (request.priority !== priorityValue) {
          return false;
        }
      }

      // Search query filter (search in title and description)
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesTitle = request.title.toLowerCase().includes(query);
        const matchesDescription = request.description
          .toLowerCase()
          .includes(query);
        const matchesId = request.id.toLowerCase().includes(query);
        if (!matchesTitle && !matchesDescription && !matchesId) {
          return false;
        }
      }

      return true;
    });
  }, [requests, selectedStatus, selectedPriority, searchQuery]);

  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {/* Filter/Action Bar */}
      <div className="flex justify-between gap-x-2.5 border-b bg-[#FAFAFA] py-1.5 pr-5 pl-14 dark:bg-[#171717]">
        <div className="flex items-end gap-x-2.5">
          <div className="w-[187px]">
            <Field>
              <FieldLabel htmlFor="search-field">Search</FieldLabel>
              <Input
                id="search-field"
                onChange={e => {
                  setSearchQuery(e.target.value);
                }}
                placeholder="Search feedback..."
                value={searchQuery}
              />
            </Field>
          </div>
          <div className="w-[187px]">
            <Label htmlFor="status-field">Status</Label>
            <div className="relative w-full">
              <Select
                onValueChange={setSelectedStatus}
                value={selectedStatus}
              >
                <SelectTrigger
                  className="w-full"
                  id="status-field"
                >
                  <span
                    className={
                      selectedStatus
                        ? 'block max-w-[calc(100%-32px)] truncate'
                        : ''
                    }
                  >
                    <SelectValue placeholder="All statuses" />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="backlog">Backlog</SelectItem>
                  <SelectItem value="blocked">Blocked</SelectItem>
                  <SelectItem value="unstarted">Todo</SelectItem>
                  <SelectItem value="started">In Progress</SelectItem>
                  <SelectItem value="completed">Done</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
              {selectedStatus && (
                <button
                  className="ring-offset-background focus:ring-ring absolute top-1/2 right-8 -translate-y-1/2 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedStatus('');
                  }}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
          <div className="w-[187px]">
            <Label htmlFor="priority-field">Priority</Label>
            <div className="relative w-full">
              <Select
                onValueChange={setSelectedPriority}
                value={selectedPriority}
              >
                <SelectTrigger
                  className="w-full"
                  id="priority-field"
                >
                  <span
                    className={
                      selectedPriority
                        ? 'block max-w-[calc(100%-32px)] truncate'
                        : ''
                    }
                  >
                    <SelectValue placeholder="All" />
                  </span>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Important</SelectItem>
                  <SelectItem value="0">Not Important</SelectItem>
                </SelectContent>
              </Select>
              {selectedPriority && (
                <button
                  className="ring-offset-background focus:ring-ring absolute top-1/2 right-8 -translate-y-1/2 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-offset-2 focus:outline-none disabled:pointer-events-none"
                  onClick={e => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSelectedPriority('');
                  }}
                  type="button"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>
        </div>
        <Separator
          className="-mt-1 h-[calc(100%+8px)]!"
          orientation="vertical"
        />
        <div className="flex items-center gap-x-2.5">
          <Button
            onClick={() => {
              setIsFeedbackDialogOpen(true);
            }}
          >
            <MessageSquareIcon />
            Send Feedback
          </Button>
        </div>
      </div>

      {/* Content Area */}
      <div className="flex min-h-0 flex-1 flex-col px-5 pt-5 md:pl-14">
        {filteredRequests.length === 0 && requests.length === 0 ? (
          <Empty className="pt-52!">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <MessageSquareIcon />
              </EmptyMedia>
              <EmptyTitle>No customer feedback yet</EmptyTitle>
              <EmptyDescription>
                No feedback has been submitted yet. Get started by sharing your
                thoughts.
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                onClick={() => {
                  setIsFeedbackDialogOpen(true);
                }}
              >
                <MessageSquareIcon />
                Send Feedback
              </Button>
            </EmptyContent>
          </Empty>
        ) : filteredRequests.length === 0 ? (
          <Empty className="pt-52!">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <MessageSquareIcon />
              </EmptyMedia>
              <EmptyTitle>No feedback matches your filters</EmptyTitle>
              <EmptyDescription>
                Try adjusting your filters to see more results.
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <div className="flex-1 overflow-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-0" />
                  <TableHead className="w-[100px] text-xs font-semibold">
                    ID
                  </TableHead>
                  <TableHead className="text-xs font-semibold">Title</TableHead>
                  <TableHead className="w-[150px] text-xs font-semibold">
                    Submitted by
                  </TableHead>
                  <TableHead className="w-[120px] text-xs font-semibold">
                    Status
                  </TableHead>
                  <TableHead className="w-[120px] text-xs font-semibold">
                    Priority
                  </TableHead>
                  <TableHead className="w-[110px] text-xs font-semibold">
                    Created
                  </TableHead>
                  <TableHead className="w-[110px] text-xs font-semibold">
                    Updated
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRequests.map(request => {
                  const { content, metadata } = parseDescription(
                    request.description
                  );
                  const hasMetadata = Object.keys(metadata).length > 0;

                  return (
                    <TableRow key={request.id}>
                      <TableCell className="w-0">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              asChild
                              size="icon"
                              variant="ghost"
                            >
                              <a
                                href={request.url}
                                rel="noopener noreferrer"
                                target="_blank"
                              >
                                <ExternalLinkIcon />
                              </a>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>Open in Linear</TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="w-[100px] font-mono text-xs">
                        {request.id}
                      </TableCell>
                      <TableCell className="font-medium">
                        <Tooltip delayDuration={300}>
                          <TooltipTrigger asChild>
                            <div className="max-w-[400px] truncate">
                              {request.title}
                            </div>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-md">
                            <div className="space-y-2 text-xs">
                              {content && (
                                <div className="whitespace-pre-wrap">
                                  {content}
                                </div>
                              )}
                              {hasMetadata && (
                                <>
                                  {content && <div className="border-t pt-2" />}
                                  <div className="space-y-1">
                                    {metadata.Email && (
                                      <div>
                                        <span className="font-semibold">
                                          Email:
                                        </span>{' '}
                                        {metadata.Email}
                                      </div>
                                    )}
                                    {metadata.Page && (
                                      <div>
                                        <span className="font-semibold">
                                          Page:
                                        </span>{' '}
                                        <span className="break-all">
                                          {metadata.Page}
                                        </span>
                                      </div>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>
                          </TooltipContent>
                        </Tooltip>
                      </TableCell>
                      <TableCell className="w-[150px]">
                        <div className="truncate">
                          {metadata['Submitted by'] || '—'}
                        </div>
                      </TableCell>
                      <TableCell className="w-[120px]">
                        <Badge
                          variant={
                            STATUS_VARIANT[request.statusType] || 'pastel-slate'
                          }
                        >
                          {request.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="w-[120px]">
                        {request.priority > 0 && (
                          <Badge
                            variant={
                              PRIORITY_VARIANT[request.priority] ||
                              'pastel-slate'
                            }
                          >
                            {PRIORITY_LABELS[request.priority] || 'None'}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="w-[110px]">
                        {formatDate(request.createdAt)}
                      </TableCell>
                      <TableCell className="w-[110px]">
                        {request.updatedAt !== request.createdAt
                          ? formatDate(request.updatedAt)
                          : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Feedback Dialog */}
      <CustomerFeedbackDialog
        onOpenChange={setIsFeedbackDialogOpen}
        onSubmitSuccess={() => {
          // Refresh the list immediately after successful submission
          void refetch();
        }}
        open={isFeedbackDialogOpen}
        userEmail={odooAuth?.login || ''}
        userName={odooAuth?.name || ''}
      />
    </div>
  );
}
